import { z } from 'zod';
import { AccountCode, CcyCodeSchema, MoneyStr, OperatorUsername } from './common.js';

export const Currency = z.object({
  code: CcyCodeSchema,
  symbol: z.string(),
  scale: z.number().int().min(0).max(8),
  position: z.enum(['before', 'after']),
  sortOrder: z.number().int(),
});
export type Currency = z.infer<typeof Currency>;

/** Добавление валюты через UI (/admin/rates). Курс к USDT обязателен сразу —
 *  без него createOrder с этой валютой упадёт в calcProfit. */
export const CreateCurrencyInput = z.object({
  code: CcyCodeSchema,
  symbol: z.string().min(1).max(8),
  scale: z.number().int().min(0).max(8).default(2),
  position: z.enum(['before', 'after']).default('before'),
  // Строковая проверка > 0 без Number() (money policy): MoneyStr уже
  // гарантирует формат -?\d+(\.\d{1,4})?, остаётся отсечь минус и нули.
  initialUsdtRate: MoneyStr.refine((v) => !v.startsWith('-') && !/^0+(\.0+)?$/.test(v), {
    message: 'курс должен быть > 0',
  }),
});
export type CreateCurrencyInput = z.infer<typeof CreateCurrencyInput>;

export const AccountKind = z.enum(['client', 'executor']);
export type AccountKind = z.infer<typeof AccountKind>;

export const Account = z.object({
  code: AccountCode,
  kind: AccountKind,
  displayName: z.string().nullable(),
  isActive: z.boolean(),
  initialBalances: z.record(CcyCodeSchema, MoneyStr).default({}),
  telegramChatId: z.string().nullable(),
  telegramChatTitle: z.string().nullable(),
  // Чат ведёт остатки с обратными знаками (обычная модель: плюс = «мы должны
  // клиенту», минус = «клиент должен нам»). Прокси chat-balances инвертирует
  // суммы такого чата перед отдачей DTO.
  invertBalance: z.boolean(),
  notes: z.string().nullable(),
  ordersCount: z.number().int().optional(),
});
export type Account = z.infer<typeof Account>;

export const CreateAccountInput = z.object({
  code: AccountCode,
  kind: AccountKind,
  displayName: z.string().max(120).nullish(),
  isActive: z.boolean().default(true),
  initialBalances: z.record(CcyCodeSchema, MoneyStr).default({}),
  telegramChatId: z.string().regex(/^-?\d+$/).nullish(),
  telegramChatTitle: z.string().max(200).nullish(),
  invertBalance: z.boolean().optional(),
  notes: z.string().max(2000).nullish(),
});
export type CreateAccountInput = z.infer<typeof CreateAccountInput>;

export const UpdateAccountInput = z.object({
  kind: AccountKind.optional(),
  displayName: z.string().max(120).nullish(),
  isActive: z.boolean().optional(),
  initialBalances: z.record(CcyCodeSchema, MoneyStr).optional(),
  telegramChatId: z.string().regex(/^-?\d+$/).nullish(),
  telegramChatTitle: z.string().max(200).nullish(),
  invertBalance: z.boolean().optional(),
  notes: z.string().max(2000).nullish(),
});
export type UpdateAccountInput = z.infer<typeof UpdateAccountInput>;

export const Operator = z.object({
  username: OperatorUsername,
  userId: z.string().uuid().nullable(),
  isActive: z.boolean(),
});
export type Operator = z.infer<typeof Operator>;

export const UsdtRate = z.object({
  ccy: CcyCodeSchema,
  rate: MoneyStr,
  // Ключ провайдера, обновившего курс последним. 'manual' = ручной override
  // оператором, любая другая строка = ключ из реестра провайдеров. null —
  // курс никогда не обновлялся (только из сидера).
  source: z.string().nullable(),
  // Если true, scheduler опрашивает primary-источник для этой валюты.
  autoManaged: z.boolean(),
  updatedAt: z.string(),
});
export type UsdtRate = z.infer<typeof UsdtRate>;

export const UpdateUsdtRateInput = z.object({
  rate: MoneyStr,
  // Опциональный флаг включения авто-обновления вместе с ручным апдейтом курса.
  autoManaged: z.boolean().optional(),
});
export type UpdateUsdtRateInput = z.infer<typeof UpdateUsdtRateInput>;

// Описание провайдера для фронта: какие валюты поддерживает, нужны ли params.
export const RateProviderInfo = z.object({
  key: z.string(),
  name: z.string(),
  supportedCcy: z.array(CcyCodeSchema),
  // true = провайдер умеет любую валюту (например XE — 226 валют), supportedCcy
  // тогда лишь подсказка для UI, привязка к любой ccy разрешена.
  supportsAnyCcy: z.boolean().default(false),
  // JSON-Schema (упрощённая) для редактора параметров. Передаётся как jsonSchema7
  // когда у провайдера есть paramsSchema. Если null — параметры не требуются.
  paramsSchema: z.record(z.string(), z.unknown()).nullable(),
});
export type RateProviderInfo = z.infer<typeof RateProviderInfo>;

// Конфигурация одного источника в цепочке fallback.
export const RateSourceConfig = z.object({
  priority: z.number().int().min(0).max(99),
  providerKey: z.string().min(1),
  params: z.record(z.string(), z.unknown()).default({}),
  enabled: z.boolean().default(true),
});
export type RateSourceConfig = z.infer<typeof RateSourceConfig>;

// PUT /ref/usdt-rates/:ccy/sources — атомарная замена цепочки.
export const UpdateRateSourcesInput = z.object({
  sources: z.array(RateSourceConfig).max(10),
});
export type UpdateRateSourcesInput = z.infer<typeof UpdateRateSourcesInput>;

// Запись истории fetch-попыток.
export const RateHistoryEntry = z.object({
  id: z.number().int(),
  ccy: CcyCodeSchema,
  rate: MoneyStr.nullable(),
  source: z.string(),
  status: z.enum(['ok', 'failed']),
  error: z.string().nullable(),
  fetchedAt: z.string(),
});
export type RateHistoryEntry = z.infer<typeof RateHistoryEntry>;

// Результат POST /ref/usdt-rates/:ccy/refresh.
export const RefreshRateResult = z.object({
  ccy: CcyCodeSchema,
  rate: MoneyStr.nullable(),
  source: z.string().nullable(),
  status: z.enum(['ok', 'failed']),
  error: z.string().nullable(),
  attempts: z.array(
    z.object({
      providerKey: z.string(),
      status: z.enum(['ok', 'failed']),
      error: z.string().nullable(),
    }),
  ),
});
export type RefreshRateResult = z.infer<typeof RefreshRateResult>;
